import React, { useState, useRef } from "react";

import useEmblaCarousel from "embla-carousel-react";
import { AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import poofAnimation from "../assets/poof2.json";
import swooshSound from "../assets/swoosh.mp3";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

import FavoritesContextMenu from "./FavoritesContextMenu";

const RecentlyWatchedCarousel = ({
  items = [],
  onItemClick,
  onItemRemove,
  viewMoreHref = null,
}) => {
  const navigate = useNavigate();
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: false,
    dragFree: true,
    skipSnaps: false,
    watchDrag: true,
    watchSlides: false, // Prevent recalculation on slide changes
  });

  const [contextMenuItem, setContextMenuItem] = useState(null);
  const [bookmarkedItems, setBookmarkedItems] = useState({});
  const [deletingItems, setDeletingItems] = useState({});
  const [brokenImages, setBrokenImages] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const longPressTimers = useRef({});
  const updateTimeoutRef = useRef(null);
  const touchStartPos = useRef({});
  const isDragging = useRef(false);

  // Track if items are being deleted to prevent them from reappearing
  const deletingItemsRef = useRef({});

  React.useEffect(() => {
    deletingItemsRef.current = deletingItems;
  }, [deletingItems]);

  // Cleanup timeouts on unmount
  React.useEffect(
    () => () => {
      // Clear all long press timers
      Object.values(longPressTimers.current).forEach((timer) =>
        clearTimeout(timer)
      );
      // Clear update timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    },
    []
  );

  // Track when items change to disable animations temporarily
  React.useEffect(() => {
    debugLog("RecentlyWatchedCarousel items changed, disabling animations");
    // Set updating flag to disable animations
    setIsUpdating(true);

    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Re-enable animations after a short delay
    updateTimeoutRef.current = setTimeout(() => {
      debugLog("Re-enabling animations");
      setIsUpdating(false);
    }, 100);
  }, [items]);

  // Handle long press start
  const handleLongPressStart = (item, e) => {
    // Don't prevent default on touch events to allow scrolling
    if (!e.touches) {
      e.preventDefault();
    }

    const itemId = item._itemId || item.id || item.stream_id;

    // Track touch start position
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    touchStartPos.current[itemId] = { x: clientX, y: clientY };
    isDragging.current = false;

    const timerId = setTimeout(() => {
      // Only trigger if not dragging
      if (!isDragging.current) {
        // Vibrate if supported
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        setContextMenuItem({ ...item, _itemId: itemId });
      }
    }, 500); // 500ms hold time

    longPressTimers.current[itemId] = timerId;
  };

  // Handle long press end
  const handleLongPressEnd = (item, e) => {
    const itemId = item._itemId || item.id || item.stream_id;

    // Check if this was a drag/scroll
    if (e && (e.changedTouches || e.clientX)) {
      const clientX = e.changedTouches
        ? e.changedTouches[0].clientX
        : e.clientX;
      const clientY = e.changedTouches
        ? e.changedTouches[0].clientY
        : e.clientY;
      const startPos = touchStartPos.current[itemId];

      if (startPos) {
        const deltaX = Math.abs(clientX - startPos.x);
        const deltaY = Math.abs(clientY - startPos.y);
        const movementThreshold = 5;

        // If moved more than threshold, it's a drag
        if (deltaX > movementThreshold || deltaY > movementThreshold) {
          isDragging.current = true;
        }
      }
    }

    if (longPressTimers.current[itemId]) {
      clearTimeout(longPressTimers.current[itemId]);
      delete longPressTimers.current[itemId];
    }

    // Clean up touch position
    delete touchStartPos.current[itemId];
  };

  // Handle play now
  const handlePlayNow = (item) => {
    setContextMenuItem(null);
    if (onItemClick) {
      onItemClick(item);
    }
  };

  // Handle remove from recently watched
  const handleRemove = async (item) => {
    const itemId = item._itemId || item.id || item.stream_id;

    try {
      // Play swoosh sound and show animation at the same time
      const audio = new Audio(swooshSound);

      audio.play().catch((e) => debugLog("Audio play failed:", e));

      // Mark item as being deleted (for animation)
      setDeletingItems((prev) => ({ ...prev, [itemId]: true }));

      // Hide context menu
      setContextMenuItem(null);

      // Wait for poof animation to play
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Clean up deleting state after animation
      setDeletingItems((prev) => {
        const newState = { ...prev };

        delete newState[itemId];

        return newState;
      });

      // Notify parent component to remove the item AFTER animation
      if (onItemRemove) {
        onItemRemove(item);
      }

      toast.success("Removed from recently watched");
    } catch (error) {
      console.error("Error removing from recently watched:", error);
      toast.error(error.message || "Failed to remove");

      // Clean up deleting state on error
      setDeletingItems((prev) => {
        const newState = { ...prev };

        delete newState[itemId];

        return newState;
      });
    }
  };

  // Handle add to watchlist
  const handleAddToWatchlist = async (item) => {
    try {
      const playlistId = localStorage.getItem("currentPlaylistId");
      const webPlayerToken = webPlayerStorage.getToken();

      if (!playlistId) {
        toast.error("Please select a playlist");
        setContextMenuItem(null);

        return;
      }

      // Make sure we have the webPlayerToken set for the proxy
      if (webPlayerToken) {
        sessionStorage.setItem(`profile_token_${playlistId}`, webPlayerToken);
      } else {
        toast.error("Please login again");
        setContextMenuItem(null);

        return;
      }

      // Determine media type - properly map to API expected values
      let mediaType = "live-tv"; // Default for recently watched is usually live TV

      // Check various fields to determine the correct media type
      if (item.mediaType) {
        // Map common variations to correct API values
        if (
          item.mediaType === "live" ||
          item.mediaType === "live-tv" ||
          item.mediaType === "livetv"
        ) {
          mediaType = "live-tv";
        } else if (item.mediaType === "movie" || item.mediaType === "vod") {
          mediaType = "movie";
        } else if (
          item.mediaType === "series" ||
          item.mediaType === "tv" ||
          item.mediaType === "show"
        ) {
          mediaType = "series";
        } else {
          ({ mediaType } = item);
        }
      } else if (item.type) {
        // Fallback to type field
        if (
          item.type === "live" ||
          item.type === "live-tv" ||
          item.type === "channel"
        ) {
          mediaType = "live-tv";
        } else if (item.type === "movie") {
          mediaType = "movie";
        } else if (
          item.type === "series" ||
          item.type === "tv" ||
          item.type === "show"
        ) {
          mediaType = "series";
        }
      } else if (item.stream_type) {
        // Check stream_type field
        if (item.stream_type === "live" || item.stream_type === "live_tv") {
          mediaType = "live-tv";
        }
      } else if (item.category_name) {
        // Check category for hints
        const category = item.category_name.toLowerCase();

        if (category.includes("movie")) {
          mediaType = "movie";
        } else if (category.includes("series") || category.includes("show")) {
          mediaType = "series";
        }
      } else if (item.container_extension) {
        // If it has container_extension, it's likely VOD
        if (
          item.container_extension === "mp4" ||
          item.container_extension === "mkv"
        ) {
          // Could be movie or series episode - default to movie if no other info
          mediaType = item.series_id ? "series" : "movie";
        }
      }

      // Get the correct media ID
      // For live TV channels, we need to use the numeric stream_id
      let mediaId = item.stream_id || item.id || item.mediaId;

      // Ensure mediaId is a string and clean it up
      mediaId = String(mediaId).trim();

      // Validate mediaId
      if (!mediaId || mediaId === "undefined" || mediaId === "null") {
        console.error("Invalid mediaId:", mediaId, "from item:", item);
        throw new Error("Invalid media ID");
      }

      const itemId = item._itemId || mediaId;

      // Add to bookmarks using the API service method with proper authentication
      const response = await apiService.createBookmark(
        playlistId,
        mediaId,
        mediaType
      );

      if (response) {
        // Update local state to show star
        setBookmarkedItems((prev) => ({
          ...prev,
          [itemId]: true,
        }));

        // Also update the item's isBookmarked flag if possible
        if (item) {
          item.isBookmarked = true;
        }

        toast.success("Added to favorites");
      }
    } catch (error) {
      // Check if the error is due to already being bookmarked
      if (error.message && error.message.toLowerCase().includes("already")) {
        toast.info("Already in favorites");
        // Update local state anyway
        const itemId =
          item._itemId || item.mediaId || item.stream_id || item.id;

        setBookmarkedItems((prev) => ({
          ...prev,
          [itemId]: true,
        }));
      } else {
        // Log full error for debugging
        console.error("Full error object:", error);
        console.error("Error response:", error.response);
        toast.error(error.message || "Failed to add to favorites");
      }
    }
    setContextMenuItem(null);
  };

  // Handle remove from watchlist
  const handleRemoveFromWatchlist = async (item) => {
    try {
      const playlistId = localStorage.getItem("currentPlaylistId");
      const webPlayerToken = webPlayerStorage.getToken();

      if (!playlistId) {
        toast.error("Please select a playlist");
        setContextMenuItem(null);

        return;
      }

      if (webPlayerToken) {
        sessionStorage.setItem(`profile_token_${playlistId}`, webPlayerToken);
      } else {
        toast.error("Please login again");
        setContextMenuItem(null);

        return;
      }

      // Get the correct media ID
      let mediaId = item.mediaId || item.stream_id || item.id;

      mediaId = String(mediaId).trim();

      if (!mediaId || mediaId === "undefined" || mediaId === "null") {
        throw new Error("Invalid media ID");
      }

      const itemId = item._itemId || mediaId;

      // Remove from bookmarks using the API service
      const response = await apiService.removeBookmark(playlistId, mediaId);

      if (response) {
        toast.success("Removed from favorites");
        // Update local state
        setBookmarkedItems((prev) => {
          const newState = { ...prev };

          delete newState[itemId];

          return newState;
        });

        // Also update the item's bookmark status if it exists
        if (item.isBookmarked !== undefined) {
          item.isBookmarked = false;
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to remove from favorites");
    }
    setContextMenuItem(null);
  };

  // Don't check for empty items - let parent control visibility
  // Even if all items are being deleted, keep the structure

  const renderImage = (item) =>
    item.logoPath ||
    item.logo_path ||
    item.logo ||
    item.stream_icon ||
    item.streamIcon ||
    item.channel_logo ||
    item.channelLogo ||
    item.icon ||
    item.image ||
    item.channelIcon ||
    item.channel_icon ||
    item.logoUrl ||
    item.logo_url ||
    item.iconUrl ||
    item.icon_url;

  return (
    <>
      <div
        className={`home-edge-row overflow-hidden select-none ${isUpdating ? "recently-watched-updating" : ""}`}
        ref={emblaRef}
        style={{ paddingTop: 10, paddingBottom: 10, width: "100%" }}
      >
        <div className="flex gap-4" style={{ minWidth: "100%", width: "100%" }}>
          {(items || []).map((item, idx) => {
            const itemId =
              item._itemId || item.id || item.stream_id || `item-${idx}`;
            const key = itemId;
            const image = renderImage(item);
            const isDeleting =
              deletingItems[itemId] || deletingItemsRef.current[itemId];

            return (
              <div
                className="recently-watched-fixed-item flex-shrink-0 w-44 sm:w-48 md:w-52 lg:w-56 xl:w-60 max-[932px]:landscape:w-[calc((100vw-96px)/4.5)]"
                key={key}
              >
                <article className="recently-watched-item transform-gpu">
                  <div className="relative group">
                    <div
                      className="relative overflow-hidden rounded-xl aspect-video cursor-pointer"
                      role="button"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                        transformOrigin: "center center",
                        boxShadow:
                          "0 10px 25px -8px rgba(0,0,0,0.4), " +
                          "0 6px 15px -4px rgba(0,0,0,0.3)",
                        transform: "scale(1)",
                        transition: isUpdating
                          ? "none"
                          : "transform 0.2s ease-out, box-shadow 0.2s ease-out",
                      }}
                      tabIndex={0}
                      onClick={() => {
                        // Only trigger click if not dragging
                        if (
                          !contextMenuItem &&
                          !isDragging.current &&
                          onItemClick
                        ) {
                          onItemClick(item);
                        }
                        isDragging.current = false;
                      }}
                      onKeyDown={activateOnEnterOrSpace}
                      onMouseDown={(e) => {
                        if (!contextMenuItem) {
                          handleLongPressStart({ ...item, _itemId: itemId }, e);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isUpdating) {
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow =
                            "0 0 0 4px rgba(100, 149, 237, 0.5)";
                          e.currentTarget.style.zIndex = "10";
                        }
                      }}
                      onMouseLeave={(e) => {
                        handleLongPressEnd({ ...item, _itemId: itemId }, e);
                        if (!isUpdating) {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 10px 25px -8px rgba(0,0,0,0.4), 0 6px 15px -4px rgba(0,0,0,0.3)";
                          e.currentTarget.style.zIndex = "auto";
                        }
                      }}
                      onMouseUp={(e) =>
                        handleLongPressEnd({ ...item, _itemId: itemId }, e)
                      }
                      onTouchEnd={(e) =>
                        handleLongPressEnd({ ...item, _itemId: itemId }, e)
                      }
                      onTouchStart={(e) => {
                        if (!contextMenuItem) {
                          handleLongPressStart({ ...item, _itemId: itemId }, e);
                        }
                      }}
                    >
                      {isDeleting ? (
                        // Show poof animation when deleting
                        <div className="relative h-full flex items-center justify-center aspect-video bg-gray-900/50">
                          <Lottie
                            animationData={poofAnimation}
                            autoplay={true}
                            loop={false}
                            style={{
                              width: "100%",
                              height: "100%",
                              maxWidth: 200,
                              maxHeight: 200,
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          {/* Bookmark star indicator - Bottom Right */}
                          {(item.isBookmarked ||
                            bookmarkedItems[
                              item.mediaId ||
                                item.stream_id ||
                                item.id ||
                                itemId
                            ]) && (
                            <div className="absolute bottom-8 right-2 z-10">
                              <span className="text-yellow-400 text-2xl">
                                ⭐
                              </span>
                            </div>
                          )}
                          <div className="relative h-full flex flex-col">
                            {/* Logo/Image Section */}
                            <div className="flex-1 flex items-center justify-center p-5 rounded-lg overflow-hidden">
                              {image && !brokenImages[itemId] ? (
                                <img
                                  alt={item.name || item.title || "Content"}
                                  className="max-h-20 max-w-full object-contain"
                                  draggable={false}
                                  src={image}
                                  onDragStart={(e) => e.preventDefault()}
                                  onError={() => {
                                    // Mark image as broken
                                    setBrokenImages((prev) => ({
                                      ...prev,
                                      [itemId]: true,
                                    }));
                                  }}
                                />
                              ) : (
                                <i className="fa-solid fa-image text-white/50 text-3xl" />
                              )}
                            </div>

                            {/* Progress Bar */}
                            <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                              <div
                                className="h-2 bg-gray-600 rounded-full overflow-hidden"
                                style={{ width: "70%" }}
                              >
                                {/* Cornflower blue progress fill representing elapsed time of current program */}
                                <div
                                  className="h-full rounded-full transition-[width] duration-300"
                                  style={{
                                    backgroundColor: "#6495ED",
                                    width: `${Math.min(Math.max(0, item.progress || 0), 100)}%`,
                                  }}
                                  title={`Progress: ${Math.round(item.progress || 0)}%`}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Program Title or Channel Name outside card at bottom */}
                    {!isDeleting && (
                      <div className="text-white text-sm font-bold truncate text-center mt-2 px-1 select-none">
                        {item.currentProgram ||
                          item.epgTitle ||
                          item.nowPlaying ||
                          item.name ||
                          item.channel_name ||
                          item.title ||
                          "Channel"}
                      </div>
                    )}
                  </div>
                </article>
              </div>
            );
          })}
          {viewMoreHref && (
            <button
              className="recently-watched-fixed-item flex-shrink-0 w-44 sm:w-48 md:w-52 lg:w-56 xl:w-60 cursor-pointer"
              type="button"
              onClick={() => navigate(viewMoreHref)}
            >
              <div className="flex aspect-video items-center justify-center rounded-xl bg-white/10 text-white text-base font-semibold">
                View More
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Full-screen Context Menu Modal */}
      <AnimatePresence>
        {contextMenuItem &&
          (() => {
            const mt =
              contextMenuItem.mediaType || contextMenuItem.type || "live-tv";
            const isLive =
              ["live-tv", "live", "livetv"].includes(mt) ||
              contextMenuItem.stream_type === "live";
            const ctxType = isLive
              ? "tv"
              : ["series", "show"].includes(mt)
                ? "series"
                : "movie";
            const favKey =
              contextMenuItem._itemId ||
              contextMenuItem.mediaId ||
              contextMenuItem.stream_id ||
              contextMenuItem.id;
            const isFav =
              bookmarkedItems[favKey] !== undefined
                ? bookmarkedItems[favKey]
                : contextMenuItem.isBookmarked;

            return (
              <FavoritesContextMenu
                actions={[
                  {
                    key: "play",
                    icon: "fa-play",
                    label: "Play Now",
                    onClick: () => handlePlayNow(contextMenuItem),
                  },
                  {
                    key: "fav",
                    icon: "fa-bookmark",
                    label: isFav ? "Clear from Favorites" : "Add to Favorites",
                    onClick: () =>
                      isFav
                        ? handleRemoveFromWatchlist(contextMenuItem)
                        : handleAddToWatchlist(contextMenuItem),
                  },
                  {
                    key: "remove",
                    icon: "fa-trash",
                    label: "Remove",
                    danger: true,
                    onClick: () => handleRemove(contextMenuItem),
                  },
                ]}
                item={contextMenuItem}
                type={ctxType}
                onClose={() => setContextMenuItem(null)}
              />
            );
          })()}
      </AnimatePresence>
    </>
  );
};

export default React.memo(RecentlyWatchedCarousel, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders
  // Only re-render if items array length or IDs change
  if (prevProps.items.length !== nextProps.items.length) {
    return false; // Props are different, should re-render
  }

  // Check if item IDs are the same
  const prevIds = prevProps.items
    .map((item) => item.mediaId || item.id || item.stream_id)
    .join(",");
  const nextIds = nextProps.items
    .map((item) => item.mediaId || item.id || item.stream_id)
    .join(",");

  if (prevIds !== nextIds) {
    return false; // Props are different, should re-render
  }

  // Check if callbacks changed
  if (
    prevProps.onItemClick !== nextProps.onItemClick ||
    prevProps.onItemRemove !== nextProps.onItemRemove ||
    prevProps.viewMoreHref !== nextProps.viewMoreHref
  ) {
    return false; // Props are different, should re-render
  }

  // Props are the same, don't re-render
  return true;
});
